import { useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Bell, AlertCircle } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { useNotifications } from '@/contexts/NotificationsContext'
import { NotificationItem } from './NotificationItem'
import { cn } from '@/lib/utils'

interface NotificationPanelProps {
  className?: string
}

export function NotificationPanel({ className }: NotificationPanelProps) {
  const { t } = useTranslation('app')
  const {
    notifications,
    lastReadAt,
    isLoading,
    error,
    hasMore,
    loadMore,
  } = useNotifications()

  const sentinelRef = useRef<HTMLDivElement>(null)

  const handleIntersect = useCallback(
    (entries: Array<IntersectionObserverEntry>) => {
      if (entries[0]?.isIntersecting && hasMore) {
        loadMore()
      }
    },
    [hasMore, loadMore],
  )

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: '100px',
    })
    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [handleIntersect])

  if (error) {
    return (
      <div
        data-testid="notification-panel"
        className={cn('flex flex-col items-center justify-center py-12 px-4', className)}
      >
        <AlertCircle className="h-8 w-8 text-destructive/40 mb-3" />
        <p className="text-sm text-destructive">{t('notifications.loadFailed')}</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div data-testid="notification-panel" className={cn('flex flex-col', className)}>
        <div className="divide-y divide-border">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="px-4 py-3">
              <Skeleton className="h-3.5 w-3/4" />
              <Skeleton className="mt-2 h-3 w-1/3" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div data-testid="notification-panel" className={cn('flex flex-col', className)}>
      <div className="max-h-[min(24rem,var(--radix-popover-content-available-height,24rem))] overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center py-12 px-4">
            <Bell className="h-8 w-8 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">{t('notifications.empty')}</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                isUnread={notification.createdAt > lastReadAt}
              />
            ))}
            <div ref={sentinelRef} className="h-1" />
          </div>
        )}
      </div>
    </div>
  )
}
